/**
 * GUEST-FIRST public catalog: explore courses without account (catalog is
 * server-authoritative, REST `/api/catalog`). Enrolling saves progress
 * locally; CREATING requires an account. Vibrant course cards.
 */
import { type Component, createSignal, For, Show } from "solid-js";
import { createSafeQuery } from "@/lib/api";
import { msg } from "@lingui/core/macro";
import { BookOpen, Search, Users } from "@fleet/icons";
import { type CatalogEntryDTO, courseQueryKeys, fetchCatalog } from "@aula/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Link } from "@tanstack/solid-router";
import { SERVER_URL } from "@/lib/runtime-config";
import { Skeleton } from "@fleet/ui";
import { Mascot } from "@/components/ui/mascot";
import { QueryErrorState } from "@/components/ui/query-error-state";

/** Deterministic joy color per course (cheerful but harmonious variety). */
const JOY_TONES = [
  { chip: "bg-joy-grape text-joy-grape-foreground", soft: "bg-joy-grape/12" },
  { chip: "bg-joy-sky text-joy-sky-foreground", soft: "bg-joy-sky/15" },
  { chip: "bg-joy-mint text-joy-mint-foreground", soft: "bg-joy-mint/15" },
  { chip: "bg-joy-sun text-joy-sun-foreground", soft: "bg-joy-sun/18" },
  { chip: "bg-joy-flame text-joy-flame-foreground", soft: "bg-joy-flame/12" },
  { chip: "bg-joy-berry text-joy-berry-foreground", soft: "bg-joy-berry/12" },
  { chip: "bg-joy-aqua text-joy-aqua-foreground", soft: "bg-joy-aqua/15" },
];
const toneFor = (id: string) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = (h * 31 + id.charCodeAt(i)) >>> 0;
  }
  return JOY_TONES[h % JOY_TONES.length]!;
};

export const CatalogView: Component = () => {
  const { t } = useI18n();
  const [q, setQ] = createSignal("");
  const catalog = createSafeQuery<CatalogEntryDTO[]>(() => ({
    queryKey: courseQueryKeys.catalogList(q()),
    queryFn: ({ signal }) => fetchCatalog(SERVER_URL, { q: q(), signal }),
    staleTime: 30_000,
  }));

  return (
    <div class="mx-auto max-w-5xl space-y-6 p-4 md:p-6">
      <div class="aula-mesh flex flex-col gap-3 rounded-3xl border border-border p-5 md:flex-row md:items-center md:justify-between md:p-6">
        <div>
          <h1 class="font-display text-2xl font-extrabold tracking-tight md:text-3xl">
            {t(msg`Catalog`)}
          </h1>
          <p class="text-sm text-muted-foreground">
            {t(msg`Browse and enroll — no account needed.`)}
          </p>
        </div>
        <div class="relative">
          <Search class="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            data-testid="catalog-search"
            value={q()}
            onInput={(e) => setQ(e.currentTarget.value)}
            placeholder={t(msg`Search courses`)}
            class="w-full rounded-2xl border-2 border-border bg-surface py-2.5 pl-10 pr-3 font-medium transition-colors focus-visible:border-accent focus-visible:outline-none md:w-72"
          />
        </div>
      </div>

      {/* P23/I3 · "no response" != "nothing here": on error `isLoading` is false,
          so the guard below passed through and the screen rendered the EMPTY state.
          We must WRAP the block, not chain another condition onto the same Show component
          (which would render both error AND skeleton). */}
      <Show when={catalog.isError}>
        <QueryErrorState testId="catalog-error" onRetry={() => void catalog.refetch()} />
      </Show>
      <Show when={!catalog.isError}>
        <Show
          when={!catalog.isLoading}
          fallback={
            <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <For each={Array.from({ length: 6 })}>
                {() => <Skeleton class="h-44 w-full rounded-3xl" />}
              </For>
            </div>
          }
        >
          <Show
            when={(catalog.data?.length ?? 0) > 0}
            fallback={
              <div
                class="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-border p-14 text-center"
                data-testid="catalog-empty"
              >
                <Mascot mood="think" class="mb-4 h-24 w-24" />
                <h3 class="font-display text-lg font-bold">{t(msg`No courses published yet`)}</h3>
                <p class="mt-1 text-sm text-muted-foreground">
                  {t(msg`Create the first one from Authoring.`)}
                </p>
              </div>
            }
          >
            <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-grid">
              <For each={catalog.data}>{(course) => <CatalogCard course={course} />}</For>
            </div>
          </Show>
        </Show>
      </Show>
    </div>
  );
};

const CatalogCard: Component<{ course: CatalogEntryDTO }> = (props) => {
  const { t } = useI18n();
  const tone = () => toneFor(props.course.id);
  return (
    <Link
      to="/catalog/$courseId"
      params={{ courseId: props.course.id }}
      class="group relative block overflow-hidden rounded-3xl border border-border bg-surface p-5 shadow-sm transition-all hover:-translate-y-1 hover:border-accent/40 hover:shadow-lg"
      data-testid="catalog-card"
    >
      <div
        class={`pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full ${tone().soft}`}
      />
      <div class="relative mb-3 flex items-center gap-2">
        <div
          class={`flex h-11 w-11 items-center justify-center rounded-2xl shadow-sm transition-transform group-hover:scale-105 ${tone().chip}`}
        >
          <BookOpen class="h-5 w-5" />
        </div>
        <Show when={props.course.category}>
          <span class={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${tone().soft}`}>
            {props.course.category}
          </span>
        </Show>
      </div>
      <h3 class="relative line-clamp-2 font-display font-bold group-hover:text-accent">
        {props.course.title}
      </h3>
      <Show when={props.course.description}>
        <p class="relative mt-1 line-clamp-2 text-sm text-muted-foreground">
          {props.course.description}
        </p>
      </Show>
      <div class="relative mt-4 flex items-center gap-4 text-xs font-medium text-muted-foreground">
        <span>{t(msg`{count} lessons`, { count: props.course.lessonCount })}</span>
        <span class="flex items-center gap-1">
          <Users class="h-3 w-3" /> {props.course.enrolledCount}
        </span>
        <Show when={props.course.authorName}>
          {(name) => <span class="ml-auto truncate">{t(msg`by {name}`, { name: name() })}</span>}
        </Show>
      </div>
    </Link>
  );
};
